import { useState, useEffect, useMemo, useRef } from 'react';
import ToolCard from '../../../common/element/Card';
import Button from '../../../common/element/Buttons';
import Alert from '../../../common/element/Alert/Alert';
import SearchInput from '../../../common/element/Search/SearchInput';
import Sidebar from './sideBar';
import ThreeDotLoader from '../../../common/element/ButtonLoader/ThreeDotLoader';

const StepComponent = ({
  stepNumber,
  completedSteps = [],
  title,
  instruction,
  requiredCount,
  availableCodes = [],
  selectedCodes,
  onSelectionChange,
  onNext,
  onCancel,
  isLastStep = false,
  isSubmitting = false,
  totalSteps = 10,
}) => {
  const nCodes = availableCodes;

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [touched, setTouched] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const selectedCodesRef = useRef(null);
  const checkboxListRef = useRef(null);

  const getDisplayText = (codeItem) =>
    typeof codeItem === 'object' ? codeItem.text : codeItem;

  const getTooltipText = (codeItem) => {
    if (typeof codeItem === 'object' && codeItem.description) {
      return codeItem.description;
    }
    return getDisplayText(codeItem);
  };

  const getCodeKey = (codeItem, index) =>
    typeof codeItem === 'object' ? codeItem.id : index;

  const isCodeSelected = (codeItem) => {
    const itemId = typeof codeItem === 'object' ? codeItem.id : codeItem;
    return selectedCodes.some((selected) => {
      const selectedId = typeof selected === 'object' ? selected.id : selected;
      return selectedId === itemId;
    });
  };

  const scrollToLatestSelected = () => {
    if (selectedCodesRef.current && selectedCodes.length > 0) {
      selectedCodesRef.current.scrollTop = selectedCodesRef.current.scrollHeight;
    }
  };

  const scrollCheckboxListToTop = () => {
    if (checkboxListRef.current) {
      checkboxListRef.current.scrollTop = 0;
    }
  };

  const filteredCodes = useMemo(() => {
    const searchValue =
      typeof searchText === 'string'
        ? searchText
        : searchText?.target?.value || '';
    if (!searchValue.trim()) return nCodes;
    return nCodes.filter((codeItem) =>
      getDisplayText(codeItem).toLowerCase().includes(searchValue.toLowerCase().trim())
    );
  }, [nCodes, searchText]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleCodeToggle = (codeItem) => {
    setTouched(true);
    const itemId = typeof codeItem === 'object' ? codeItem.id : codeItem;

    const isSelected = selectedCodes.some((selected) => {
      const selectedId = typeof selected === 'object' ? selected.id : selected;
      return selectedId === itemId;
    });

    if (isSelected) {
      const newSelection = selectedCodes.filter((selected) => {
        const selectedId = typeof selected === 'object' ? selected.id : selected;
        return selectedId !== itemId;
      });
      onSelectionChange(newSelection);
      setAlertVisible(false);
      return;
    }

    // Step 1 has no upper limit; all other steps have an exact required count
    if (stepNumber !== 1 && selectedCodes.length >= requiredCount) {
      setAlertMessage(`You can only select ${requiredCount} items for this step.`);
      setAlertType('danger');
      setAlertVisible(true);
      return;
    }

    const newSelection = [...selectedCodes, codeItem];
    onSelectionChange(newSelection);

    setTimeout(() => scrollToLatestSelected(), 100);

    if (stepNumber === 1 && newSelection.length === requiredCount) {
      setAlertMessage(
        `You have selected the minimum ${requiredCount} items. You can select more if needed.`
      );
      setAlertType('success');
      setAlertVisible(true);
    } else if (stepNumber !== 1 && newSelection.length === requiredCount) {
      setAlertMessage(`You have selected the required ${requiredCount} items.`);
      setAlertType('success');
      setAlertVisible(true);
    } else {
      setAlertVisible(false);
    }
  };

  const handleMouseEnter = (e, codeItem) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: getTooltipText(codeItem),
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    setAlertVisible(false);
    setAlertMessage('');
    setAlertType('info');
    setTouched(false);
    setSearchText('');
    setTimeout(() => scrollCheckboxListToTop(), 100);
  }, [stepNumber]);

  const canProceed = selectedCodes.length >= requiredCount;

  return (
    <div className="flex-1 bg-white">
      <ToolCard>
        <div className="rounded-lg p-3 md:p-6 pt-6 md:pt-8 text-start">

          {/* Mobile hamburger button — hidden on md+ */}
          <div className="flex justify-between items-center md:hidden mb-4">
            <span className="font-lato font-semibold text-[14px] text-black">
              Step {stepNumber} of {totalSteps}
            </span>
            <button
              className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Toggle steps menu"
            >
              <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black"/>
                <path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black"/>
                <path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black"/>
              </svg>
            </button>
          </div>

          {/* Step indicator (horizontal on desktop, overlay on mobile) */}
          <Sidebar
            totalSteps={totalSteps}
            currentStep={stepNumber}
            completedSteps={completedSteps}
            isMobileOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          />

          <h2 className="font-lato font-bold text-[15px] md:text-[20px] lg:text-[24px] leading-[130%] md:leading-[115%] text-black mb-4 md:mb-8">
            {instruction}
          </h2>

          <div className="flex justify-between flex-col md:flex-row gap-2 items-start md:items-center mb-6 md:mb-10">
            <p className="font-lato font-bold text-[13px] md:text-[16px] lg:text-[20px] leading-[115%] text-black mb-0">
              Baselines Assessment  Selected: {selectedCodes.length}
            </p>
            <SearchInput
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search Baselines Assessment "
            />
          </div>

          {touched && alertVisible && (
            <div className="mb-6">
              <Alert
                type={alertType}
                showIcon
                closable
                onClose={() => setAlertVisible(false)}
              >
                {alertMessage}
              </Alert>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Baseline grid */}
            <div className="lg:col-span-2">
              {searchText && (
                <div className="mb-4 text-sm text-gray-600">
                  Showing {filteredCodes.length} of {nCodes.length} Baselines Assessment 
                  {filteredCodes.length === 0 && (
                    <span className="text-[#FF8E1C] ml-2">No matches found</span>
                  )}
                </div>
              )}
              <div
                ref={checkboxListRef}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-4 gap-2 md:gap-3 max-h-[40vh] md:max-h-[45vh] overflow-y-auto cstm-col"
                style={{ scrollBehavior: 'smooth' }}
              >
                {filteredCodes.map((codeItem, index) => {
                  const selected = isCodeSelected(codeItem);
                  return (
                    <button
                      key={getCodeKey(codeItem, index)}
                      onClick={() => handleCodeToggle(codeItem)}
                      onMouseEnter={(e) => handleMouseEnter(e, codeItem)}
                      onMouseLeave={handleMouseLeave}
                      className={`flex items-center justify-center text-center px-2 md:px-4 py-2 md:py-3 rounded-[10px] md:rounded-[12px] h-[38px] md:h-[42px] border transition-all font-lato font-semibold text-[12px] md:text-[13px] lg:text-[14px] leading-[18px] ${
                        selected
                          ? 'border-[#FF8E1C] bg-[#FF8E1C] text-white'
                          : 'border-[#FF8E1C] bg-white text-black hover:bg-[#FFF9F3]'
                      }`}
                    >
                      <span className="truncate">{index + 1}. {getDisplayText(codeItem)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Baselines Assessment  panel */}
            <div className="lg:col-span-1">
              <h3 className="font-lato font-semibold text-[15px] md:text-[18px] lg:text-[24px] leading-[115%] text-black mb-3 md:mb-6">
                Selected Baselines Assessment 
              </h3>
              <div
                ref={selectedCodesRef}
                className="space-y-2 md:space-y-3 max-h-[30vh] md:max-h-[40vh] overflow-y-auto"
                style={{ scrollBehavior: 'smooth' }}
              >
                {selectedCodes.length === 0 ? (
                  <div className="flex items-center justify-center h-[120px] md:h-[200px] border-2 border-dashed border-gray-200 rounded-[8px]">
                    <p className="text-gray-400 font-lato text-[13px] md:text-[16px]">
                      No Baselines Assessment selected yet
                    </p>
                  </div>
                ) : (
                  selectedCodes.map((codeItem, index) => (
                    <div
                      key={getCodeKey(codeItem, index)}
                      className="text-center font-semibold text-[12px] md:text-[14px] font-lato p-2 bg-[#FF8E1C] text-white rounded-lg"
                    >
                      {index + 1}. {getDisplayText(codeItem)}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-start md:justify-end space-x-3 md:space-x-4 mt-5 md:mt-8">
            <Button
              onClick={onCancel}
              className="bg-[#FFF4E5] text-black px-[14px] md:px-[18px] py-3 md:py-4 w-[120px] md:w-[150px] h-11 md:h-14 rounded-[12px] font-lato font-bold text-[14px] md:text-[16px] leading-[150%]"
            >
              Back
            </Button>
            <Button
              onClick={onNext}
              disabled={!canProceed || isSubmitting}
              className={`px-[14px] md:px-[18px] py-3 md:py-4 w-[120px] md:w-[150px] h-11 md:h-14 rounded-[12px] font-lato font-bold text-[14px] md:text-[16px] leading-[150%] transition-all duration-200 ${
                canProceed && !isSubmitting
                  ? 'bg-[#FF8E1C] text-white hover:bg-[#E67E0C]'
                  : 'bg-[#FF8E1C] text-white opacity-80 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? <ThreeDotLoader /> : (isLastStep ? 'Submit' : 'Next')}
            </Button>
          </div>

        </div>
      </ToolCard>

      {/* Tooltip — fixed so it renders outside any scroll container */}
      {tooltip.visible && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, calc(-100% - 12px))',
          }}
        >
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-4 max-w-[240px] text-sm text-gray-700 text-center leading-relaxed">
            {tooltip.text}
          </div>
          {/* downward arrow */}
          <div className="flex justify-center">
            <div
              className="w-0 h-0"
              style={{
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid white',
                filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.08))',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StepComponent;
